import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { getApiBase, getAuthHeaders } from '../../utils/api';

const AirportSearch = ({ value, onChange, placeholder }) => {
  const [inputValue, setInputValue] = useState(value || '');
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const wrapperRef = useRef(null);

  // Handle clicks outside to close suggestions
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        setShowSuggestions(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Sync with parent value
  useEffect(() => {
    setInputValue(value || '');
  }, [value]);

  // Debounced search
  useEffect(() => {
    const searchAirports = async () => {
      const keyword = inputValue.trim();

      // Minimum 3 characters
      if (keyword.length < 3) {
        setSuggestions([]);
        setShowSuggestions(false);
        setError('');
        return;
      }

      setLoading(true);
      setError('');

      try {
        const apiBase = getApiBase();
        const headers = getAuthHeaders();

        const response = await axios.get(`${apiBase}/search/airports/duffel`, {
          params: { keyword },
          headers
        });

        if (response.data.success && response.data.data?.data) {
          setSuggestions(response.data.data.data);
          setShowSuggestions(true);
        } else {
          setSuggestions([]);
          setError(response.data.error || 'No airports found');
        }
      } catch (err) {
        console.error('Airport search error:', err);
        setError('Failed to search airports');
        setSuggestions([]);
      } finally {
        setLoading(false);
      }
    };

    // Debounce the search
    const timeoutId = setTimeout(searchAirports, 300);
    return () => clearTimeout(timeoutId);
  }, [inputValue]);

  const handleInputChange = (e) => {
    const newValue = e.target.value;
    setInputValue(newValue);
    onChange(newValue); // Update parent with raw input
  };

  const handleSelectAirport = (airport) => {
    const iataCode = airport.iataCode || '';
    setInputValue(iataCode);
    onChange(iataCode);
    setShowSuggestions(false);
    setSuggestions([]);
  };

  const formatAirportName = (airport) => {
    const name = airport.name || '';
    const city = airport.address?.cityName || '';
    const country = airport.address?.countryName || '';
    const iata = airport.iataCode || '';

    if (city && country) {
      return `${name}, ${city}, ${country} (${iata})`;
    }
    return `${name} (${iata})`;
  };

  return (
    <div ref={wrapperRef} className="relative">
      <input
        type="text"
        value={inputValue}
        onChange={handleInputChange}
        placeholder={placeholder || 'Enter airport code or city name'}
        className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
      />

      {loading && (
        <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
          <div className="animate-spin h-4 w-4 border-2 border-blue-500 border-t-transparent rounded-full"></div>
        </div>
      )}

      {error && inputValue.length >= 3 && (
        <div className="absolute left-0 right-0 mt-1 bg-red-50 border border-red-200 rounded-md px-3 py-2 text-xs text-red-600 z-10">
          {error}
        </div>
      )}

      {showSuggestions && suggestions.length > 0 && (
        <div className="absolute left-0 right-0 mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-y-auto z-20">
          {suggestions.map((airport, index) => (
            <div
              key={airport.id || index}
              onClick={() => handleSelectAirport(airport)}
              className="px-3 py-2 hover:bg-blue-50 cursor-pointer border-b border-gray-100 last:border-b-0"
            >
              <div className="flex justify-between items-start">
                <div className="flex-1">
                  <div className="text-sm font-medium text-gray-900">
                    {airport.iataCode && (
                      <span className="inline-block bg-blue-100 text-blue-800 px-2 py-0.5 rounded text-xs font-semibold mr-2">
                        {airport.iataCode}
                      </span>
                    )}
                    {airport.name}
                  </div>
                  <div className="text-xs text-gray-500 mt-1">
                    {airport.address?.cityName && `${airport.address.cityName}, `}
                    {airport.address?.countryName}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {inputValue.length > 0 && inputValue.length < 3 && (
        <div className="absolute left-0 right-0 mt-1 bg-yellow-50 border border-yellow-200 rounded-md px-3 py-2 text-xs text-yellow-700 z-10">
          Enter at least 3 characters to search
        </div>
      )}
    </div>
  );
};

export default AirportSearch;
